import React, { useState, useEffect, useCallback } from 'react';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import {
  fetchAssessments,
  fetchResults,
  PsicosocialAssessment,
  PsicosocialResultRow,
  LevelCounts,
} from '../lib/psicosocial-persistencia';
import { PSICOSOCIAL_DIMENSIONS } from '../lib/psicosocial-instrumento';
import { generatePsicosocialResultsPDF } from '../lib/psicosocial-resultados-pdf';
import { plural } from '../lib/plurales';

// Mismo estilo base que PsicosocialCodigosPage.tsx.
const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 6, color: '#e2e8f0', padding: '8px 10px', marginTop: 4,
};

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 4, color: '#e2e8f0', fontWeight: 600, fontSize: 13,
};

const cardStyle: React.CSSProperties = {
  background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 16, marginBottom: 20,
};

const fieldDescStyle: React.CSSProperties = {
  fontSize: 12.5, color: '#94a3b8', margin: '0 0 8px 0', lineHeight: 1.45,
};

// Colores por nivel de riesgo — misma convención que NIVEL_COLORES_NIOSH
// (NioshMethodologyPage.tsx) y NIVEL_COLORES de RULA/REBA: bajo=verde,
// medio=ámbar, alto=rojo. Las barras usan `text` (color sólido); bg y
// border se usan en la leyenda.
type Nivel = keyof LevelCounts;

const NIVELES: Nivel[] = ['bajo', 'medio', 'alto'];

const NIVEL_COLORES_PSICO: Record<Nivel, { bg: string; border: string; text: string; label: string }> = {
  bajo:  { bg: 'rgba(47,191,113,0.12)', border: 'rgba(47,191,113,0.3)', text: '#2FBF71', label: 'Bajo' },
  medio: { bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)', text: '#f59e0b', label: 'Medio' },
  alto:  { bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.3)',  text: '#ef4444', label: 'Alto' },
};

function sumLevelCounts(list: LevelCounts[]): LevelCounts {
  return list.reduce(
    (acc, c) => ({ bajo: acc.bajo + c.bajo, medio: acc.medio + c.medio, alto: acc.alto + c.alto }),
    { bajo: 0, medio: 0, alto: 0 }
  );
}

function pct(n: number, total: number): number {
  return total === 0 ? 0 : Math.round((n / total) * 100);
}

function formatEcuadorDateTime(iso: string): string {
  return new Date(iso).toLocaleString('es-EC', {
    timeZone: 'America/Guayaquil', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// Barra horizontal apilada de 100%: un segmento por nivel, ancho
// proporcional al número de personas en ese nivel.
function StackedBar({ counts, height = 14 }: { counts: LevelCounts; height?: number }) {
  const total = counts.bajo + counts.medio + counts.alto;
  return (
    <div
      style={{ display: 'flex', height, borderRadius: 4, overflow: 'hidden', background: '#1e293b' }}
      title={NIVELES.map(n => `${NIVEL_COLORES_PSICO[n].label}: ${counts[n]} (${pct(counts[n], total)}%)`).join(' · ')}
    >
      {NIVELES.map(n => counts[n] > 0 && (
        <div key={n} style={{ width: `${(counts[n] / total) * 100}%`, background: NIVEL_COLORES_PSICO[n].text }} />
      ))}
    </div>
  );
}

function LevelCountsText({ counts }: { counts: LevelCounts }) {
  return (
    <span style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>
      {NIVELES.map((n, i) => (
        <React.Fragment key={n}>
          {i > 0 && ' · '}
          <span style={{ color: NIVEL_COLORES_PSICO[n].text, fontWeight: 600 }}>{counts[n]}</span>
        </React.Fragment>
      ))}
    </span>
  );
}

function Legend() {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
      {NIVELES.map(n => {
        const c = NIVEL_COLORES_PSICO[n];
        return (
          <span key={n} style={{
            fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 20,
            color: c.text, background: c.bg, border: `1px solid ${c.border}`,
          }}>
            Riesgo {c.label.toLowerCase()}
          </span>
        );
      })}
    </div>
  );
}

function UnitResultBlock({ row }: { row: PsicosocialResultRow }) {
  return (
    <div style={{ borderTop: '1px solid #1e293b', padding: '14px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0' }}>{row.unitName}</span>
        <span style={{ fontSize: 12.5, color: '#94a3b8' }}>
          {row.respondents} de {row.headcount} trabajadores respondieron ({pct(row.respondents, row.headcount)}%)
        </span>
      </div>

      {row.suppressed || row.distributions === null ? (
        <p style={{ ...fieldDescStyle, margin: 0 }}>
          Suprimida: {row.respondents} de {row.headcount} respondieron (mínimo {row.minGroup} para proteger el anonimato).
        </p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 220px) 1fr auto', gap: '8px 12px', alignItems: 'center' }}>
          {PSICOSOCIAL_DIMENSIONS.map(dim => {
            const counts = row.distributions!.dimensions[dim.key];
            return (
              <React.Fragment key={dim.key}>
                <span style={{ fontSize: 12.5, color: '#cbd5e1' }}>{dim.label}</span>
                {counts ? <StackedBar counts={counts} /> : <span style={{ fontSize: 12, color: '#64748b' }}>Sin datos</span>}
                {counts ? <LevelCountsText counts={counts} /> : <span />}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function PsicosocialResultadosPage() {
  const { company } = useCompany();
  const { impersonated } = useImpersonation();
  const displayCompany = impersonated ?? company;
  const companyId = displayCompany?.id ?? '';

  const [assessments, setAssessments] = useState<PsicosocialAssessment[]>([]);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState('');
  const [loadingAssessments, setLoadingAssessments] = useState(true);

  const [results, setResults] = useState<PsicosocialResultRow[]>([]);
  const [loadingResults, setLoadingResults] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const loadAssessments = useCallback(async () => {
    if (!companyId) { setLoadingAssessments(false); return; }
    setLoadingAssessments(true);
    try {
      const rows = await fetchAssessments(companyId);
      setAssessments(rows);
      if (rows.length > 0) {
        setSelectedAssessmentId(prev => (prev && rows.some(r => r.id === prev)) ? prev : rows[0].id);
      }
    } catch (e: any) {
      setError(e.message ?? 'Error al cargar las evaluaciones');
    } finally {
      setLoadingAssessments(false);
    }
  }, [companyId]);

  useEffect(() => { loadAssessments(); }, [loadAssessments]);

  const selectedAssessment = assessments.find(a => a.id === selectedAssessmentId) ?? null;
  const isClosed = selectedAssessment?.status === 'cerrada';

  // Solo se consultan resultados de evaluaciones cerradas: antes del
  // cierre psychosocial_results no tiene filas para esa evaluación.
  useEffect(() => {
    let cancelled = false;
    setResults([]);
    setError(null);
    setPdfError(null);
    setLoadingResults(false);
    if (!selectedAssessmentId || !isClosed) return;

    setLoadingResults(true);
    fetchResults(selectedAssessmentId)
      .then(rows => { if (!cancelled) setResults(rows); })
      .catch((e: any) => { if (!cancelled) setError(e.message ?? 'Error al cargar los resultados'); })
      .finally(() => { if (!cancelled) setLoadingResults(false); });

    return () => { cancelled = true; };
  }, [selectedAssessmentId, isClosed]);

  const publishedUnits = results.filter(r => !r.suppressed && r.distributions !== null);
  const suppressedUnits = results.filter(r => r.suppressed);
  const globalCounts = sumLevelCounts(publishedUnits.map(r => r.distributions!.global));
  const globalTotal = globalCounts.bajo + globalCounts.medio + globalCounts.alto;

  // Solo descarga local (doc.save): el informe no se sube a ningún bucket.
  const handleDownloadPdf = () => {
    if (!selectedAssessment) return;
    setPdfError(null);
    try {
      generatePsicosocialResultsPDF({
        assessment: selectedAssessment,
        results,
        companyName: displayCompany?.name ?? 'Empresa',
        companyRuc: displayCompany?.ruc ?? null,
      });
    } catch (e: any) {
      setPdfError(e.message ?? 'Error al generar el PDF');
    }
  };

  if (loadingAssessments) return <div style={{ padding: 24, color: '#94a3b8' }}>Cargando…</div>;

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <h1 style={{ fontSize: 22, marginBottom: 6, color: '#e2e8f0' }}>Cuestionario Psicosocial — Resultados</h1>
      <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>
        Distribución agregada de niveles de riesgo por unidad, calculada al cerrar la evaluación. No se muestran
        respuestas individuales.
      </p>

      {error && (
        <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* ===== Selección de evaluación ===== */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 12 }}>Evaluación</h2>
        {assessments.length === 0 ? (
          <p style={{ ...fieldDescStyle, margin: 0 }}>
            Todavía no hay evaluaciones. Créalas desde "Cuestionario Psicosocial".
          </p>
        ) : (
          <>
            <label style={labelStyle}>Evaluación</label>
            <select style={inputStyle} value={selectedAssessmentId} onChange={e => setSelectedAssessmentId(e.target.value)}>
              {assessments.map(a => (
                <option key={a.id} value={a.id} style={{ color: '#000' }}>
                  {a.title} ({a.status})
                </option>
              ))}
            </select>
          </>
        )}
      </div>

      {selectedAssessment && !isClosed && (
        <div style={cardStyle}>
          <p style={{ ...fieldDescStyle, margin: 0 }}>Los resultados se calculan al cerrar la evaluación.</p>
        </div>
      )}

      {selectedAssessment && isClosed && !error && (loadingResults ? (
        <div style={cardStyle}>
          <p style={{ ...fieldDescStyle, margin: 0 }}>Cargando resultados…</p>
        </div>
      ) : (
        <>
          {results.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              {pdfError && <span style={{ fontSize: 12.5, color: '#fca5a5' }}>{pdfError}</span>}
              <button
                onClick={handleDownloadPdf}
                style={{
                  background: '#2FBF71', color: '#0f172a', border: 'none', borderRadius: 6,
                  padding: '8px 16px', fontWeight: 600, cursor: 'pointer',
                }}
              >
                Descargar PDF
              </button>
            </div>
          )}

          {/* ===== Resumen global ===== */}
          <div style={cardStyle}>
            <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 4 }}>Resumen global</h2>
            <p style={fieldDescStyle}>
              Nivel de riesgo global de {plural(globalTotal, 'persona', 'personas')} en {plural(publishedUnits.length, 'unidad', 'unidades')} con resultados
              {suppressedUnits.length > 0 && ` — no incluye ${plural(suppressedUnits.length, 'unidad suprimida', 'unidades suprimidas')}`}.
              {results.length > 0 && ` Calculado el ${formatEcuadorDateTime(results[0].computedAt)}.`}
            </p>
            <Legend />
            {globalTotal === 0 ? (
              <p style={{ ...fieldDescStyle, margin: 0 }}>
                Ninguna unidad alcanzó el mínimo de respuestas para publicar resultados.
              </p>
            ) : (
              <>
                <StackedBar counts={globalCounts} height={22} />
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
                  {NIVELES.map(n => (
                    <span key={n} style={{ fontSize: 13, color: '#cbd5e1' }}>
                      <span style={{ color: NIVEL_COLORES_PSICO[n].text, fontWeight: 700 }}>{NIVEL_COLORES_PSICO[n].label}</span>
                      {': '}{globalCounts[n]} ({pct(globalCounts[n], globalTotal)}%)
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* ===== Resultados por unidad ===== */}
          <div style={cardStyle}>
            <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 4 }}>Resultados por unidad</h2>
            <p style={fieldDescStyle}>
              Cada barra muestra cuántas personas de la unidad quedaron en riesgo bajo, medio o alto en esa dimensión
              (verde · ámbar · rojo).
            </p>
            {results.length === 0 ? (
              <p style={{ ...fieldDescStyle, margin: 0 }}>Esta evaluación no tiene resultados guardados.</p>
            ) : (
              results.map(row => <UnitResultBlock key={row.id} row={row} />)
            )}
          </div>
        </>
      ))}
    </div>
  );
}
